import { userService } from '@/services/user.service';
import { exportService } from '@/services/export.service';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  CheckCircle,
  Edit,
  Plus,
  Shield,
  ShieldCheck,
  BookOpen,
  Users,
  Trash2,
  X,
  Download
} from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import SearchInputDebounced from '@/components/ui/SearchInputDebounced';
import { Column } from '@/components/ui/DataTablePaginated';
import TooltipArrow from '@/components/ui/TooltipArrow';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import RoleForm, { RoleFormData } from '@/components/forms/RoleForm';
import Pagination from '@/components/ui/Pagination';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { ROLES_PERMISSION } from '@/constants/constant-permissions';

// Enhanced API role interface with statistics
interface ApiRole {
  _id: string;
  role_key: string;
  role_name: string;
  role_description?: string;
  is_active?: boolean;
  createdAt: string;
  updatedAt: string;
  __v?: number;
  // Enhanced fields from actual API response
  roleId: string;
  roleName: string;
  roleKey: string;
  totalUsers: number;
  assignedCourses: number;
  avgProgress: number;
  completionRate: number;
  performanceMetrics: {
    avgScore: number;
    avgTimeSpent: number;
    engagementLevel: string;
  };
  userStats: {
    completed: number;
    inProgress: number;
    notStarted: number;
    overdue: number;
  };
  certification_count: number;
  certification_stats: {
    completed: number;
    in_progress: number;
    not_started: number;
  };
  role_course_assignments?: number;
  role_certification_assignments?: number;
  role_learning_path_assignments?: number;
}

interface Role {
  id: string;
  roleKey: string;
  name: string;
  description: string;
  usersCount: number;
  coursesAssigned: number;
  certificationCount: number;
  completionRate: number;
  averageScore: number;
  lastUpdated: string;
  roleCourseAssignments: number;
  roleCertificationAssignments: number;
  roleLearningPathAssignments: number;
}

export default function Roles() {
  usePermissionRouteAccess([
    ROLES_PERMISSION.ADMIN_ROLES_READ,
    ROLES_PERMISSION.ADMIN_ROLES_WRITE,
  ]);
  const canWrite = useHasPermission([ROLES_PERMISSION.ADMIN_ROLES_WRITE]);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, DEFAULT_DEBOUNCE_DELAY);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [exportLoading, setExportLoading] = useState<'csv' | 'excel' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['roles', debouncedSearch, currentPage, pageSize],
    queryFn: () => userService.getRoles({
      role_name: debouncedSearch || undefined,
      skip: (currentPage - 1) * pageSize,
      limit: pageSize
    }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: RoleFormData) => userService.createRole(data),
    onSuccess: () => {
      toast.success('Role created successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setShowAddModal(false);
      setIsSubmitting(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.error_description || 'Failed to create role');
      setIsSubmitting(false);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: Partial<RoleFormData> }) =>
      userService.updateRole(key, data),
    onSuccess: () => {
      toast.success('Role updated successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setShowEditModal(false);
      setSelectedRole(null);
      setIsSubmitting(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.error_description || 'Failed to update role');
      setIsSubmitting(false);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (key: string) => userService.deleteRole(key),
    onSuccess: () => {
      toast.success('Role deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setShowDeleteModal(false);
      setSelectedRole(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.error_description || 'Failed to delete role');
      setIsSubmitting(false);
    }
  });

  const roles: Role[] = useMemo(() => {
    const apiRoles: ApiRole[] = data?.data || [];
    return apiRoles.map((r: ApiRole) => ({
      id: r._id,
      roleKey: r.roleKey || r.role_key,
      name: r.roleName || r.role_name,
      description: r.role_description || 'No description available',
      usersCount: r.totalUsers || 0,
      coursesAssigned: r.assignedCourses || 0,
      certificationCount: r.certification_count || 0,
      completionRate: Math.round(r.completionRate || 0),
      averageScore: Math.round(r.performanceMetrics?.avgScore || 0),
      lastUpdated: format(new Date(r.updatedAt), 'PPpp'),
      roleCourseAssignments: r.role_course_assignments || 0,
      roleCertificationAssignments: r.role_certification_assignments || 0,
      roleLearningPathAssignments: r.role_learning_path_assignments || 0,
    }));
  }, [data]);

  const metrics = useMemo(() => {
    return {
      totalRoles: data?.meta_data?.metrics_data?.total_roles || 'N/A',
      rolesInUse: data?.meta_data?.metrics_data?.roles_with_users || 'N/A',
      totalUsers: data?.meta_data?.metrics_data?.total_users_with_roles || 'N/A',
      totalCourseAssignments: data?.meta_data?.metrics_data?.total_role_course_assignments || 0,
      totalCertificationAssignments: data?.meta_data?.metrics_data?.total_role_certification_assignments || 0,
      totalLearningPathAssignments: data?.meta_data?.metrics_data?.total_role_learning_path_assignments || 0,
    };
  }, [data, roles]);

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setExportLoading(format);

      // Call the export service with format and debounced search query (no skip/limit)
      const result = await exportService.exportRolesList(format, debouncedSearch);

      if (result.success) {
        toast.success(`${result.count} roles exported to ${format.toUpperCase()} successfully`);
      } else {
        toast.error(result.message || 'No data to export');
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.message || 'Failed to export roles');
    } finally {
      setExportLoading(null);
    }
  };

  const totalPages = Math.ceil((data?.pagination?.total_records || data?.meta_data?.total_records || roles.length) / pageSize);
  const totalItems = data?.pagination?.total_records || data?.meta_data?.total_records || roles.length;

  // Table columns
  const columns: Column<Role>[] = [
    {
      key: 'name',
      header: 'Role',
      accessor: (role) => (
        <div>
          <p className="font-medium text-gray-900">{role.name}</p>
          <p className="text-xs text-gray-500 mt-1">
            Key: {role.roleKey} | Updated: {format(new Date(role.lastUpdated), 'MMM dd, yyyy')}
          </p>
        </div>
      ),
      className: 'min-w-[200px]'
    },
    {
      key: 'users',
      header: 'Users',
      accessor: (role) => (
        <div className="flex items-center justify-center gap-1">
          <Users className="w-4 h-4 text-gray-400" />
          <span className="font-medium">{role.usersCount}</span>
        </div>
      ),
      align: 'center'
    },
    {
      key: 'courses',
      header: 'Courses',
      accessor: (role) => (
        <div className="flex items-center justify-center gap-1">
          <BookOpen className="w-4 h-4 text-gray-400" />
          <span className="font-medium">{role.coursesAssigned}</span>
        </div>
      ),
      align: 'center'
    },
    {
      key: 'certifications',
      header: 'Certifications',
      accessor: (role) => (
        <div className="flex items-center justify-center gap-1">
          <Award className="w-4 h-4 text-gray-400" />
          <span className="font-medium">{role.certificationCount}</span>
        </div>
      ),
      align: 'center'
    },

    {
      key: 'actions',
      header: 'Actions',
      accessor: (role) => (
        <div className="flex items-center justify-center gap-2">
          <TooltipArrow content={!canWrite ? 'You do not have permission' : 'Assign Permissions'}>
            <button
              onClick={() => canWrite && navigate(
                `/admin/roles/${role.roleKey}/permissions`,
                { state: { from: '/admin/roles', roleName: role.name } }
              )}
              disabled={!canWrite}
              className="p-1.5 text-brand-emerald-600 hover:text-brand-emerald-700 hover:bg-brand-emerald-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldCheck className="w-4 h-4" />
            </button>
          </TooltipArrow>
          <TooltipArrow content={!canWrite ? 'You do not have permission' : 'View Courses'}>
            <button
              onClick={() => canWrite && navigate(
                `/admin/roles/${role.roleKey}/courses`,
                { state: { from: '/admin/roles' } }
              )}
              disabled={!canWrite}
              className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BookOpen className="w-4 h-4" />
            </button>
          </TooltipArrow>
          <TooltipArrow content={!canWrite ? 'You do not have permission' : 'Edit Role'}>
            <button
              onClick={() => canWrite && openEditModal(role)}
              disabled={!canWrite}
              className="p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Edit className="w-4 h-4" />
            </button>
          </TooltipArrow>
          <TooltipArrow content={!canWrite ? 'You do not have permission' : 'Delete Role'}>
            <button
              onClick={() => canWrite && openDeleteModal(role)}
              disabled={!canWrite}
              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </TooltipArrow>
        </div>
      ),
      align: 'center',
      className: 'min-w-[150px]'
    }
  ];

  const handleCreate = async (data: RoleFormData) => {
    setIsSubmitting(true);
    await createMutation.mutateAsync(data);
  };

  const handleUpdate = async (data: RoleFormData) => {
    if (!selectedRole) return;
    setIsSubmitting(true);
    await updateMutation.mutateAsync({
      key: selectedRole.roleKey,
      data: {
        role_name: data.role_name,
        role_description: data.role_description
      }
    });
  };

  const handleDelete = async () => {
    if (!selectedRole) return;
    setIsSubmitting(true);
    await deleteMutation.mutateAsync(selectedRole.roleKey);
    setIsSubmitting(false);
  };

  const openEditModal = (role: Role) => {
    setSelectedRole(role);
    setShowEditModal(true);
  };

  const openDeleteModal = (role: Role) => {
    setRoleToDelete({ id: role.id, name: role.name });
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page on page size change
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold mb-2">Roles Management</h1>
            <p className="text-brand-purple-100 text-sm sm:text-base">
              Define roles and competency requirements for your organization
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => canWrite && setShowAddModal(true)}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 bg-white text-brand-purple-600 rounded-lg transition-colors text-sm sm:text-base flex-1 sm:flex-initial justify-center ${
                canWrite ? 'hover:bg-brand-purple-50' : 'opacity-50 cursor-not-allowed'
              }`}
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Create Role</span>
            </button>
            <button
              onClick={() => canWrite && navigate('/admin/roles/assign-permissions')}
              disabled={!canWrite}
              title={!canWrite ? 'You do not have permission' : undefined}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-white border border-white/30 rounded-lg transition-colors text-sm sm:text-base flex-1 sm:flex-initial justify-center ${
                canWrite ? 'bg-white/20 hover:bg-white/30' : 'bg-white/10 opacity-50 cursor-not-allowed'
              }`}
            >
              <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Assign Permissions</span>
            </button>
          </div>
        </div>

        {/* Metrics */}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white/20 backdrop-blur-sm rounded-lg p-3 animate-pulse">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="h-3 bg-white/30 rounded w-20 mb-2"></div>
                    <div className="h-6 sm:h-8 bg-white/30 rounded w-12"></div>
                  </div>
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white/30 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-purple-100 text-xs">Total Roles</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{metrics.totalRoles}</p>
                </div>
                <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-brand-purple-200" />
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-purple-100 text-xs">Roles In Use</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{metrics.rolesInUse}</p>
                </div>
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-brand-purple-200" />
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-purple-100 text-xs">Count of Users with Roles</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{metrics.totalUsers}</p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-brand-purple-200" />
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
              <div>
                <p className="text-brand-purple-100 text-xs mb-2">Total Assignments</p>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">{metrics.totalCourseAssignments}</p>
                    <p className="text-xs text-brand-purple-200 mt-0.5">Courses</p>
                  </div>
                  <div className="h-8 w-px bg-brand-purple-300/30"></div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">{metrics.totalCertificationAssignments}</p>
                    <p className="text-xs text-brand-purple-200 mt-0.5">Certs</p>
                  </div>
                  <div className="h-8 w-px bg-brand-purple-300/30"></div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">{metrics.totalLearningPathAssignments}</p>
                    <p className="text-xs text-brand-purple-200 mt-0.5">Paths</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Search and Export */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <SearchInputDebounced
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search roles by name"
              debounceDelay={300}
              loading={isLoading}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => canWrite && handleExport('csv')}
              disabled={!canWrite || exportLoading !== null}
              title={!canWrite ? 'You do not have permission' : undefined}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading === 'csv' ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              {exportLoading === 'csv' ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={() => canWrite && handleExport('excel')}
              disabled={!canWrite || exportLoading !== null}
              title={!canWrite ? 'You do not have permission' : undefined}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading === 'excel' ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              {exportLoading === 'excel' ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-${column.align || 'left'} text-xs font-medium text-gray-500 uppercase tracking-wider ${column.className || ''}`}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                [...Array(pageSize)].map((_, i) => (
                  <tr key={i}>
                    {columns.map((column) => (
                      <td key={column.key} className="px-4 py-4">
                        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                    No roles found. Create your first role to get started.
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <tr key={role.id} className="hover:bg-gray-50">
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-4 py-4 text-${column.align || 'left'} ${column.className || ''}`}
                      >
                        {column.accessor(role)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && roles.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handlePageSizeChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* Add Role Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create New Role</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <RoleForm
              onSubmit={handleCreate}
              isSubmitting={isSubmitting}
              onCancel={() => setShowAddModal(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && selectedRole && (
        <div className="fixed inset-0  bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Role</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedRole(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <RoleForm
              initialData={{
                role_key: selectedRole.roleKey,
                role_name: selectedRole.name,
                role_description: selectedRole.description
              }}
              onSubmit={handleUpdate}
              isSubmitting={isSubmitting}
              isEditing
              onCancel={() => {
                setShowEditModal(false);
                setSelectedRole(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Delete Role Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedRole(null);
          setRoleToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Role"
        description={selectedRole && selectedRole.usersCount > 0
          ? `Are you sure you want to delete this role? This role is assigned to ${selectedRole.usersCount} users.`
          : "Are you sure you want to delete this role?"
        }
        itemName={roleToDelete?.name}
        isLoading={isSubmitting}
      />
    </div>
  );
}